/**
 * RoleQueueRoute.tsx
 *
 * Destination: client/src/core/client/admin/routes/Moderate/Queue/RoleQueueRoute.tsx
 *
 * Moderation queue that lists all comments whose author has a selected role.
 * Follows the same Relay pagination-container pattern as ApprovedQueueRoute.tsx.
 */

import { Localized } from "@fluent/react/compat";
import React, { FunctionComponent, useCallback, useState } from "react";
import { graphql, RelayPaginationProp } from "react-relay";

import parseModerationOptions from "coral-framework/helpers/parseModerationOptions";
import { IntersectionProvider } from "coral-framework/lib/intersection";
import { Flex } from "coral-ui/components/v2";
import {
  LOCAL_ID,
  lookup,
  useLocal,
  withPaginationContainer,
} from "coral-framework/lib/relay";
import { createRouteConfig } from "coral-framework/lib/router";
import { GQLUSER_ROLE } from "coral-framework/schema";

import { RoleQueueRoute_query } from "coral-admin/__generated__/RoleQueueRoute_query.graphql";
import { RoleQueueRouteLocal } from "coral-admin/__generated__/RoleQueueRouteLocal.graphql";
import {
  RoleQueueRoutePaginationQueryVariables,
  SectionFilter,
} from "coral-admin/__generated__/RoleQueueRoutePaginationQuery.graphql";

import EmptyMessage from "./EmptyMessage";
import LoadingQueue from "./LoadingQueue";
import Queue from "./Queue";

// ─── Role selector ────────────────────────────────────────────────────────────

const ROLE_OPTIONS: Array<{ value: GQLUSER_ROLE; label: string }> = [
  { value: GQLUSER_ROLE.STAFF, label: "Staff" },
  { value: GQLUSER_ROLE.MODERATOR, label: "Moderator" },
  { value: GQLUSER_ROLE.ADMIN, label: "Admin" },
];

interface RoleSelectorProps {
  selectedRole: GQLUSER_ROLE;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const RoleSelector: FunctionComponent<RoleSelectorProps> = ({
  selectedRole,
  onChange,
}) => (
  <div
    style={{
      padding: "12px 24px",
      borderBottom: "1px solid rgba(0,0,0,0.12)",
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    <label
      htmlFor="roleQueueSelect"
      style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}
    >
      Filter by role:
    </label>
    <select
      id="roleQueueSelect"
      value={selectedRole}
      onChange={onChange}
      style={{ fontSize: 13 }}
    >
      {ROLE_OPTIONS.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  query: RoleQueueRoute_query;
  relay: RelayPaginationProp;
  storyID?: string | null;
  siteID?: string | null;
  section?: SectionFilter | null;
}

const danglingLogic = (_status: string) => false;

export const RoleQueueRoute: FunctionComponent<Props> = (props) => {
  const [selectedRole, setSelectedRole] = useState<GQLUSER_ROLE>(
    GQLUSER_ROLE.STAFF
  );
  const [disableLoadMore, setDisableLoadMore] = useState(false);
  const [isRefetchingRole, setIsRefetchingRole] = useState(false);

  const [{ moderationQueueSort }] = useLocal<RoleQueueRouteLocal>(graphql`
    fragment RoleQueueRouteLocal on Local {
      moderationQueueSort
    }
  `);

  const handleRoleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const role = e.target.value as GQLUSER_ROLE;
      setSelectedRole(role);
      setIsRefetchingRole(true);
      props.relay.refetchConnection(
        5,
        () => setIsRefetchingRole(false),
        {
          role,
          count: 5,
          orderBy: moderationQueueSort,
          storyID: props.storyID,
          siteID: props.siteID,
          section: props.section,
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.relay, props.storyID, props.siteID, props.section, moderationQueueSort]
  );

  const loadMore = useCallback(() => {
    if (!props.relay.hasMore() || props.relay.isLoading()) {
      return;
    }
    setDisableLoadMore(true);
    props.relay.loadMore(10, (error) => {
      setDisableLoadMore(false);
      if (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });
  }, [props.relay]);

  if (!props.query || !props.query.viewer) {
    return null;
  }

  if (isRefetchingRole) {
    return (
      <Flex direction="column">
        <RoleSelector selectedRole={selectedRole} onChange={handleRoleChange} />
        <LoadingQueue />
      </Flex>
    );
  }

  const comments = props.query.commentsByRole.edges.map((edge) => edge.node);

  return (
    <Flex direction="column">
      <RoleSelector selectedRole={selectedRole} onChange={handleRoleChange} />
      <IntersectionProvider>
        <Queue
          settings={props.query.settings}
          viewer={props.query.viewer}
          comments={comments}
          onLoadMore={loadMore}
          hasLoadMore={props.relay.hasMore()}
          disableLoadMore={disableLoadMore}
          danglingLogic={danglingLogic}
          emptyElement={
            <Localized id="moderate-emptyQueue-byRole">
              <EmptyMessage>
                There are no comments from users with this role.
              </EmptyMessage>
            </Localized>
          }
          showStoryInfo={!props.storyID}
        />
      </IntersectionProvider>
    </Flex>
  );
};

// ─── Relay pagination container ───────────────────────────────────────────────

// TODO: (cvle) If this could be autogenerated...
type FragmentVariables = RoleQueueRoutePaginationQueryVariables;

const enhanced = withPaginationContainer<
  Props,
  RoleQueueRoutePaginationQueryVariables,
  FragmentVariables
>(
  {
    query: graphql`
      fragment RoleQueueRoute_query on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 5 }
        cursor: { type: "Cursor" }
        storyID: { type: "ID" }
        siteID: { type: "ID" }
        section: { type: "SectionFilter" }
        orderBy: { type: "COMMENT_SORT", defaultValue: CREATED_AT_DESC }
        role: { type: "USER_ROLE!", defaultValue: STAFF }
      ) {
        commentsByRole(
          role: $role
          storyID: $storyID
          siteID: $siteID
          section: $section
          first: $count
          after: $cursor
          orderBy: $orderBy
        ) @connection(key: "RoleQueueRoute_commentsByRole", filters: ["role"]) {
          edges {
            node {
              id
              site {
                id
              }
              author {
                commentsRejectedOnSites
                allCommentsRejected
              }
              ...ModerateCardContainer_comment
            }
          }
        }
        settings {
          ...ModerateCardContainer_settings
        }
        viewer {
          ...ModerateCardContainer_viewer
        }
      }
    `,
  },
  {
    getConnectionFromProps(props) {
      return props.query && props.query.commentsByRole;
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        ...fragmentVariables,
        count,
        cursor,
      };
    },
    query: graphql`
      query RoleQueueRoutePaginationQuery(
        $storyID: ID
        $siteID: ID
        $section: SectionFilter
        $count: Int!
        $cursor: Cursor
        $orderBy: COMMENT_SORT
        $role: USER_ROLE!
      ) {
        ...RoleQueueRoute_query
          @arguments(
            storyID: $storyID
            siteID: $siteID
            section: $section
            count: $count
            cursor: $cursor
            orderBy: $orderBy
            role: $role
          )
      }
    `,
  }
)(RoleQueueRoute);

export const routeConfig = createRouteConfig<Props, RoleQueueRoute_query>({
  Component: enhanced,
  query: graphql`
    query RoleQueueRouteQuery(
      $storyID: ID
      $siteID: ID
      $section: SectionFilter
      $initialOrderBy: COMMENT_SORT
      $role: USER_ROLE!
    ) {
      ...RoleQueueRoute_query
        @arguments(
          storyID: $storyID
          siteID: $siteID
          section: $section
          orderBy: $initialOrderBy
          role: $role
        )
    }
  `,
  prepareVariables: (params, match) => {
    const initialOrderBy = lookup(
      match.context.relayEnvironment,
      LOCAL_ID
    )!.moderationQueueSort;
    const { storyID, siteID, section } = parseModerationOptions(match);
    return {
      ...params,
      storyID,
      siteID,
      section,
      initialOrderBy,
      role: GQLUSER_ROLE.STAFF,
    };
  },
  cacheConfig: { force: true },
  render: ({ Component, data, match }) => {
    if (Component && data) {
      const { storyID, siteID, section } = parseModerationOptions(match);
      return (
        <Component
          query={data}
          storyID={storyID}
          siteID={siteID}
          section={section}
        />
      );
    }
    return <LoadingQueue />;
  },
});

export default enhanced;
